import { useCallback, useMemo } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { useData } from './useData';
import { TABLES } from '../constants/supabaseConstants';

/**
 * Centralized hook for creating vendors with required vendor_type_id.
 * Use this instead of directly inserting into master_vendors to ensure
 * all vendors have a vendor type assigned.
 */
export const useVendorCreate = () => {
  const session = useSession();
  const userId = session?.user?.id;
  const { insert: insertVendor } = useData(TABLES.MASTER_VENDORS);

  // Fetch vendor types for dropdowns
  const { data: vendorTypes } = useData(TABLES.REFERENCE_VENDOR_TYPES, {
    select: 'id, name, category, display_order',
    realtime: false,
  });

  // Group vendor types by category for dropdown display
  const vendorTypesGrouped = useMemo(() => {
    if (!vendorTypes) return [];
    const grouped = {};
    vendorTypes.forEach(type => {
      const category = type.category || 'אחר';
      if (!grouped[category]) {
        grouped[category] = { category, types: [] };
      }
      grouped[category].types.push(type);
    });
    // Sort types within each category
    Object.values(grouped).forEach(group => {
      group.types.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    });
    return Object.values(grouped).sort((a, b) => a.category.localeCompare(b.category, 'he'));
  }, [vendorTypes]);

  /**
   * Create a new vendor with required vendor type.
   * @param {Object} vendorData - Vendor data including name, vendor_type_id, etc.
   * @returns {Promise<{success: boolean, vendor?: Object, error?: string}>}
   */
  const createVendor = useCallback(
    async vendorData => {
      // Validate required fields
      if (!vendorData.name?.trim()) {
        return { success: false, error: 'נדרש שם ספק' };
      }
      if (!vendorData.vendor_type_id) {
        return { success: false, error: 'נדרש סוג ספק' };
      }
      if (!userId) {
        return { success: false, error: 'משתמש לא מחובר' };
      }

      try {
        const result = await insertVendor({
          name: vendorData.name.trim(),
          vendor_type_id: vendorData.vendor_type_id,
          website: vendorData.website || null,
          contact_person: vendorData.contact_person || null,
          contact_email: vendorData.contact_email || null,
          contact_phone: vendorData.contact_phone || null,
          notes: vendorData.notes || null,
          source: vendorData.source || 'user-generated',
          created_by: userId,
        });

        const newVendor = result?.[0];
        if (!newVendor?.id) {
          return { success: false, error: 'שגיאה ביצירת הספק' };
        }

        return { success: true, vendor: newVendor };
      } catch (error) {
        console.error('[useVendorCreate] Error creating vendor:', error);
        return { success: false, error: error.message || 'שגיאה ביצירת הספק' };
      }
    },
    [insertVendor, userId],
  );

  return {
    createVendor,
    vendorTypes,
    vendorTypesGrouped,
  };
};

export default useVendorCreate;
